import { baseApi } from "./baseApi";

export interface UserPosition {
  id: string;
  name: string;
  badge: string;
  rank: number;
}

export interface RespectSummaryResponse {
  success: boolean;
  data: {
    userId: string;
    username: string;
    // respect balance is intentionally omitted from the regular response —
    // it's hidden from users while we calibrate the scoring system.
    // only admins see raw numbers via the admin overview endpoint.
    reputation: {
      reputation: number;
      tier: string;
      badge: string;
      computedAt: string;
    } | null;
    position: UserPosition | null;
  };
}

export interface RespectHistoryResponse {
  success: boolean;
  data: Array<{
    _id: string;
    type: string;
    respectDelta: number;
    timestamp: string;
    metadata?: Record<string, unknown>;
  }>;
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface RespectLeaderboardResponse {
  success: boolean;
  data: Array<{
    _id: string;
    username: string;
    respect: {
      balance: number;
      locked: number;
    };
    reputation: number;
    tier: string;
  }>;
}

/** One rival, always read from the viewer's side of the pair. */
export interface RivalryRow {
  userId: string;
  username: string | null;
  tookFromYou: number;
  youTookFromThem: number;
  gaveToYou: number;
  youGaveToThem: number;
  totalMovement: number;
  net: number;
  lastMovementAt: string | null;
}

export interface RespectRivalriesResponse {
  success: boolean;
  data: {
    rivals: RivalryRow[];
    /** Took the most off you. Null until somebody has. */
    nemesis: RivalryRow | null;
    /** Handed you the most. Null until somebody has. */
    benefactor: RivalryRow | null;
  };
}

/** Which history earned a genesis line; picks the icon on the genesis page. */
export type GenesisSourceKey =
  | "base"
  | "aotd_year"
  | "podium_year"
  | "race_wins"
  | "cap_adjustment";

/** One line of a starting balance. */
export interface GenesisRow {
  source: GenesisSourceKey;
  label: string;
  year: number | null;
  amount: number;
  /** AOTDs authored, contested wins; null where nothing was counted. */
  count: number | null;
  /** Podium finish, 1-3; null on every other row. */
  rank: number | null;
}

export interface GenesisViewer {
  userId: string;
  username: string;
  rows: GenesisRow[];
  subtotal: number;
  total: number;
  capped: boolean;
}

export interface GenesisLedgerRow {
  userId: string;
  username: string;
  total: number;
  capped: boolean;
}

export interface GenesisResponse {
  success: boolean;
  data: {
    /** "pending" until Season Zero has actually been applied. */
    status: "pending" | "ready";
    generatedAt: string | null;
    appliedAt: string | null;
    totalSupply: number;
    allocated: number;
    treasury: number;
    viewer: GenesisViewer | null;
    ledger: GenesisLedgerRow[];
  };
}

export interface PositionEntry {
  username: string;
  rank: number;
  position: {
    id: string;
    name: string;
    badge: string;
  };
}

export interface PositionsResponse {
  success: boolean;
  data: {
    calculatedAt: string;
    totalUsers: number;
    users: PositionEntry[];
  };
}

// admin types — only used in RespectAdminPanel

export interface AdminUserEntry {
  userId: string;
  username: string;
  rank: number;
  position: {
    id: string;
    name: string;
    badge: string;
    order: number;
  };
  totalEarned: number;
  balance: number;
  locked: number;
  badgeCount: number;
}

export interface AdminTierBreakdown {
  tier: {
    id: string;
    name: string;
    badge: string;
    order: number;
    threshold: number;
  };
  userCount: number;
}

export interface RespectPoolState {
  baseAmount: number;
  perUserAmount: number;
  activeUserCount: number;
  totalSupply: number;
  distributed: number;
  available: number;
  distributionMultiplier: number;
  lastRebalance: string;
  updatedAt?: string;
}

export interface RespectBreakdownEntry {
  type: string;
  total: number;
  count: number;
  latestAt: string | null;
}

export interface RespectAdminOverviewResponse {
  success: boolean;
  data: {
    calculatedAt: string;
    totalUsers: number;
    tierBreakdown: AdminTierBreakdown[];
    users: AdminUserEntry[];
    pool: RespectPoolState;
  };
}

export interface RespectAdminBreakdownResponse {
  success: boolean;
  data: {
    userId: string;
    username: string;
    breakdown: RespectBreakdownEntry[];
  };
}

export interface AdjustRespectRequest {
  userId: string;
  delta: number;
  reason: string;
}

export const respectApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getRespectSummary: builder.query<RespectSummaryResponse, void>({
      query: () => "/v2/respect",
      providesTags: ["Respect"],
    }),
    getRespectHistory: builder.query<
      RespectHistoryResponse,
      { limit?: number; offset?: number } | undefined
    >({
      query: (params) => ({
        url: "/v2/respect/history",
        ...(params ? { params } : {}),
      }),
      providesTags: ["Respect"],
    }),
    getRespectPublicHistory: builder.query<
      { success: boolean; data: any[] },
      { limit?: number; offset?: number } | undefined
    >({
      query: (params) => ({
        url: "/v2/respect/history/public",
        ...(params ? { params } : {}),
      }),
      providesTags: ["Respect"],
    }),
    getRespectLeaderboard: builder.query<RespectLeaderboardResponse, void>({
      query: () => "/v2/respect/leaderboard",
      providesTags: ["Respect"],
    }),
    getRespectRivalries: builder.query<RespectRivalriesResponse, void>({
      query: () => "/v2/respect/rivalries",
      providesTags: ["Respect"],
    }),
    getGenesis: builder.query<GenesisResponse, void>({
      query: () => "/v2/respect/genesis",
      providesTags: ["Respect"],
    }),
    getPositions: builder.query<PositionsResponse, void>({
      query: () => "/v2/respect/positions",
      providesTags: ["Respect"],
    }),
    // admin endpoints — require admin role
    getRespectAdminOverview: builder.query<RespectAdminOverviewResponse, void>({
      query: () => "/v2/respect/admin/overview",
      providesTags: ["Respect"],
    }),
    getRespectAdminBreakdown: builder.query<RespectAdminBreakdownResponse, string>({
      query: (userId) => `/v2/respect/admin/breakdown/${userId}`,
      providesTags: ["Respect"],
    }),
    adjustRespect: builder.mutation<
      { success: boolean; message: string },
      AdjustRespectRequest
    >({
      query: (body) => ({
        url: "/v2/respect/admin/adjust",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Respect"],
    }),
    initializeRespect: builder.mutation<
      { success: boolean; message: string; data: any },
      void
    >({
      query: () => ({
        url: "/v2/respect/admin/initialize",
        method: "POST",
      }),
      invalidatesTags: ["Respect"],
    }),
    recalculatePositions: builder.mutation<
      { success: boolean; message: string; data: any },
      void
    >({
      query: () => ({
        url: "/v2/respect/admin/recalculate-positions",
        method: "POST",
      }),
      invalidatesTags: ["Respect"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetRespectSummaryQuery,
  useGetRespectHistoryQuery,
  useGetRespectPublicHistoryQuery,
  useGetRespectLeaderboardQuery,
  useGetRespectRivalriesQuery,
  useGetGenesisQuery,
  useGetPositionsQuery,
  useGetRespectAdminOverviewQuery,
  useGetRespectAdminBreakdownQuery,
  useAdjustRespectMutation,
  useInitializeRespectMutation,
  useRecalculatePositionsMutation,
} = respectApi;
